<template>
  <el-form
    label-position="top"
    :model="recipientInfoForm"
    ref="recipientInfoForm"
    :rules="rules"
    label-width="80px"
    size="normal"
  >
    <div test-id="main">
      <el-form-item label="Bank Name" prop="bankName">
        <el-select
          class="w-full"
          filterable
          remote
          v-model="recipientInfoForm.bankName"
          placeholder="Search Bank"
          :remote-method="
            (query) => remoteMethodBanks(query, recipientInfoForm.bankCountry)
          "
          :loading="loading"
          @change="changeBankName"
        >
          <el-option
            v-for="(bank, index) in bankOptions"
            :key="index"
            :label="bank.name"
            :value="bank.name"
          >
            {{ bank.name }}
          </el-option>
          <i slot="prefix" class="el-input__icon el-icon-search"></i>
        </el-select>
      </el-form-item>

      <!-- GHS ONLY -->
      <div v-if="secondaryCurrencyIsGHS">
        <el-form-item label="Bank Branch" prop="bankBranch">
          <el-input
            class="w-full"
            v-model="recipientInfoForm.bankBranch"
            placeholder="Enter Bank Branch"
          ></el-input>
        </el-form-item>
      </div>
      <!-- common -->
      <div class="md:grid flex flex-col md:grid-cols-4 gap-x-3">
        <div class="grid col-span-2">
          <el-form-item label="Account Number" prop="accountNo">
            <el-input
              class="w-full"
              @keydown.space.native.prevent
              min="1"
              v-model.trim="recipientInfoForm.accountNo"
              placeholder="Enter Account Number"
              type="number"
            ></el-input>
          </el-form-item>
        </div>
        <div class="grid col-span-2">
          <el-form-item label="Account Name" prop="accountName">
            <el-input
              class="w-full"
              v-if="isFrancDestinationCurrency"
              min="1"
              v-model="recipientInfoForm.accountName"
              placeholder="Enter Account Name"
              type="text"
            ></el-input>
            <el-input
              class="w-full"
              v-else
              min="1"
              :readonly="isFallBackAccountName"
              v-model="recipientInfoForm.accountName"
              placeholder="Enter Account Name"
              type="text"
            ></el-input>
          </el-form-item>
        </div>
      </div>

      <!-- XOF and XOF Only --->
      <div>
        <el-form-item label="Country" prop="bankAccountCountry">
          <el-select
            class="w-full"
            filterable
            v-model="recipientInfoForm.bankAccountCountry"
            placeholder="Search country"
          >
            <el-option
              v-for="(country, index) in allCountries"
              :key="index"
              :label="country.name"
              :value="country.code2"
              :disabled="country.disabled"
            >
              <el-tooltip
                content="We do not offer service to this country"
                placement="top"
                :disabled="!country.disabled"
              >
                <span>
                  {{ country.name }}
                </span>
              </el-tooltip>
            </el-option>
            <i slot="prefix" class="el-input__icon el-icon-search"></i>
          </el-select>
        </el-form-item>
      </div>

      <!-- Recipient Info (XOF) -->
      <div v-if="isFrancDestinationCurrency">
        <p class="font-bold text-xs mt-4 mb-2 userDetails">Recipient Details</p>
        <hr />
        <el-form-item label="Name" prop="recipientName">
          <el-input
            class="w-full"
            v-model="recipientInfoForm.recipientName"
            placeholder="Enter Recipient Name"
          ></el-input>
        </el-form-item>

        <el-form-item label="Country" prop="recipientCountry">
          <el-select
            class="w-full"
            filterable
            v-model="recipientInfoForm.recipientCountry"
            placeholder="Search country"
            @change="changeRecipientCountry"
          >
            <el-option
              v-for="(country, index) in allCountries"
              :key="index"
              :label="country.name"
              :value="country.code2"
              :disabled="country.disabled"
            >
              <el-tooltip
                content="We do not offer service to this country"
                placement="top"
                :disabled="!country.disabled"
              >
                <span>
                  {{ country.name }}
                </span>
              </el-tooltip>
            </el-option>
            <i slot="prefix" class="el-input__icon el-icon-search"></i>
          </el-select>
        </el-form-item>
        <div class="md:grid flex flex-col md:grid-cols-4 gap-x-3">
          <div class="grid col-span-2">
            <el-form-item label="District" prop="recipientDistrict">
              <el-input
                class="w-full"
                v-model="recipientInfoForm.recipientDistrict"
                placeholder="Enter Recipient District"
              ></el-input>
            </el-form-item>
          </div>
          <div class="grid col-span-2">
            <el-form-item label="City" prop="recipientCity">
              <el-input
                class="w-full"
                v-model="recipientInfoForm.recipientCity"
                placeholder="Enter Recipient City"
              ></el-input>
            </el-form-item>
          </div>
        </div>
        <el-form-item label="Address" prop="recipientAddress">
          <el-input
            class="w-full"
            v-model="recipientInfoForm.recipientAddress"
            placeholder="Enter Recipient Address"
          ></el-input>
        </el-form-item>
        <el-form-item label="Postal Code" prop="recipientPostalCode">
          <el-input
            class="w-full"
            v-model.trim="recipientInfoForm.recipientPostalCode"
            placeholder="Enter Recipient Zip Code"
          ></el-input>
        </el-form-item>
        <el-form-item label="Phone Number" prop="recipientPhoneNumber">
          <el-input
            class="w-full"
            v-model="recipientInfoForm.recipientPhoneNumber"
            placeholder="Enter Recipient Phone Number"
          ></el-input>
        </el-form-item>
      </div>
    </div>
    <el-button
      @click="validateRecipientInfoForm('recipientInfoForm')"
      class="bg-primary w-full text-white font-normal rounded-lg py-5 shadow-lg"
      :disabled="isLoading"
      :loading="isLoading"
    >
      {{ isLoading ? 'Resolving account name...' : 'Continue' }}
    </el-button>
  </el-form>
</template>

<script>
import {
  ALL_COUNTRIES,
  ALL_NIGERIAN_BANKS,
  NSANO_BANKS
} from '@/utils/constants.js'
import country from '@/mixins/getCountryDetails'
import {
  ValidateInput,
  ValidateSwiftCode,
  ValidateText,
  ValidateSingleSpace
} from '@/utils/helpers.js'
import terminal from '@/mixins/terminal.js'
import localDeposit from '@/mixins/localDeposit.js'
import tenant from '@/mixins/tenant.js'
export default {
  mixins: [country, terminal, localDeposit, tenant],
  data () {
    return {
      nigerianBanks: ALL_NIGERIAN_BANKS,
      loading: false,
      allCountries: ALL_COUNTRIES,
      nsanoBanks: NSANO_BANKS,
      isNigerianBank: false,
      isLoading: false,
      recipientInfoForm: {
        accountName: '',
        accountNo: '',
        bankName: '',
        bankCode: '',
        recipientCountry: '',
        bankCountry: '',
        // xof & xaf
        bankAccountNumber: '',
        recipientName: '',
        recipientAddress: '',
        recipientPostalCode: '',
        recipientState: '',
        recipientCity: '',
        recipientDistrict: '',
        bankAccountCountry: '',
        recipientPhoneNumber: ''
      },
      rules: {
        accountNo: [
          {
            required: true,
            message: 'Please input account number',
            trigger: ['blur', 'change']
          },
          { validator: this.validateAccountNo, trigger: ['blur', 'change'] },
          { validator: ValidateSingleSpace, trigger: ['blur', 'change'] }
        ],
        accountName: [
          {
            required: true,
            message: 'Please input account name',
            trigger: ['blur', 'change']
          },
          { validator: ValidateInput, trigger: ['blur'] }
        ],
        recipientName: [
          {
            required: true,
            message: 'Please input recipient first name',
            trigger: ['blur', 'change']
          },
          { validator: ValidateInput, trigger: ['blur'] }
        ],
        recipientAddress: [
          {
            required: true,
            message: 'Please input recipient address',
            trigger: ['blur', 'change']
          },
          { validator: ValidateInput, trigger: ['blur'] }
        ],
        recipientPhone: [
          {
            required: true,
            message: 'Please input recipient phone number',
            trigger: ['blur', 'change']
          },
          { validator: ValidateInput, trigger: ['blur'] }
        ],
        recipientPostalCode: [
          {
            required: true,
            message: 'Please input recipient zip code',
            trigger: ['blur', 'change']
          },
          { validator: ValidateSwiftCode, trigger: ['blur'] }
        ],
        recipientCountry: [
          {
            required: true,
            message: 'Please select country',
            trigger: ['blur', 'change']
          },
          { validator: ValidateInput, trigger: ['blur'] }
        ],
        recipientDistrict: [
          {
            required: true,
            message: 'Please input district name',
            trigger: ['blur', 'change']
          },
          { validator: ValidateInput, trigger: ['blur'] }
        ],
        recipientCity: [
          {
            required: true,
            message: 'Please input city name',
            trigger: ['blur', 'change']
          },
          { validator: ValidateText, trigger: ['blur'] }
        ],
        bankName: [
          {
            required: true,
            message: "Please input Bank's name",
            trigger: ['blur', 'change']
          },
          { validator: ValidateInput, trigger: ['blur', 'change'] }
        ],
        bankBranch: [
          {
            required: true,
            message: 'Please enter a branch Name',
            trigger: ['blur', 'change']
          },
          { validator: ValidateInput, trigger: ['blur', 'change'] }
        ]
      }
    }
  },
  computed: {
    supportedBanksProviderIsZeepay () {
      return this.supportedBanksProvider === 'zeepay'
    },
    isFrancDestinationCurrency () {
      return this.secondaryCurrencyIsXAF || this.secondaryCurrencyIsXOF
    }
  },
  methods: {
    next () {
      this.$emit('next')
    },
    changeRecipientCountry () {
      this.recipientInfoForm.recipientCity = ''
    },
    changeAccountNumber () {
      this.isAccountNameError = ''
    },
    async resolveAccountName (accountNo) {
      this.recipientInfoForm.accountName = '...'
      let selectedBank
      if (this.country2.shortCode === 'NG') {
        selectedBank = this.bankOptions.find(
          (bank) => bank.name === this.recipientInfoForm.bankName
        )
      } else if (this.country2.shortCode === 'GH') {
        selectedBank = this.nsanoBanks.find(
          (bank) => bank.name === this.recipientInfoForm.bankName
        )
      }
      if (!selectedBank) {
        this.$notify.error({
          title: 'Bank name not valid',
          message: 'Please re-select from list of banks',
          position: 'top-right'
        })
        return
      }
      this.resolveAccountNameCore(accountNo)
    },
    changeBankName (bankName) {
      this.recipientInfoForm.bankCode = this.bankOptions.find(
        (bank) => bank.name === bankName
      ).code
      if (
        this.computeValidAccountNumberLength(
          this.recipientInfoForm.accountNo.length
        )
      ) {
        this.resolveAccountName(this.recipientInfoForm.accountNo)
      } else {
        this.recipientInfoForm.accountNo = ''
      }
    }
  },
  watch: {
    'recipientInfoForm.accountNo': function () {
      this.isAccountNameError = ''
    },
    deep: true
  },
  async beforeMount () {
    this.prefillLocalDetails()
    this.recipientInfoForm.accountNo = this.recipientInfo.accountNo
  }
}
</script>
